import { useEffect } from "react";
import { useAuth } from "@/contexts/authContext";
import {
  FeatureFlag,
  useFeatureFlag,
  useFeatureFlagsReady,
} from "@/contexts/launchDarklyContext";
import { setMetricsDestination, setMetricsGate } from "@/lib/metrics";

/**
 * Opens the metrics gate once `midship-analytics` is authoritative for the
 * signed-in workspace. Renders nothing; mount once inside the auth and
 * LaunchDarkly providers.
 *
 * The flag is targeted per workspace so the rollout can go a percentage at a
 * time, which means its value is only meaningful after LaunchDarkly has
 * re-identified with the real workspace context — `useFeatureFlag` fails closed
 * until then. Waiting on `useFeatureFlagsReady` avoids reading that transient
 * `false` as a decision; the module holds events until this fires.
 *
 * Keyed on the workspace as well as the flag, so that logging out and back in
 * re-decides the gate: `resetMetrics()` returns it to pending on logout, and
 * without the workspace in the dependencies nothing here would fire again to
 * reopen it.
 *
 * Also points the module at the Amplitude project the server chose for this
 * workspace (MS-3413). The routing rule is not repeated here: a second copy
 * would put a build-time environment string in charge of whether a browser
 * writes to the project customer-health automation reads.
 */
export function MetricsGate(): null {
  const ready = useFeatureFlagsReady();
  const enabled = useFeatureFlag(FeatureFlag.midshipAnalytics);
  const { workspace, analyticsDestination } = useAuth();
  const workspaceId = workspace?.id ?? null;
  const destination = analyticsDestination ?? null;

  useEffect(() => {
    // Signed out there is no workspace for a per-workspace rule to target, so
    // the gate stays pending and events buffer until sign-in resolves one.
    if (!workspaceId) return;
    if (!ready) return;
    // Before the gate, so the client exists to receive whatever the opening
    // gate drains out of the buffer. No live client — the server declined to
    // name a project, or this build holds no key for the one it named — is
    // do-not-track however the flag reads.
    const live = setMetricsDestination(destination);
    setMetricsGate(enabled && live);
  }, [workspaceId, ready, enabled, destination]);

  return null;
}
